/**
 * PRE Bundle Manager Page
 *
 * Single page that handles the full export/import lifecycle for the canonical
 * PRE rule bundle:
 *   - Export: one-click download of the live DB as a signed canonical JSON.
 *   - Import wizard: Upload → Validate → Diff Review → Confirm Apply.
 *
 * Apply is gated server-side by PRE_BUNDLE_APPLY_ENABLED and locally requires
 * the operator to type the bundle checksum and source environment to confirm.
 */
import { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from '@tanstack/react-router';
import {
  Card,
  CardContent,
  Button,
  Badge,
  Input,
  cn,
} from '@respectly/ui';
import {
  Package,
  ArrowLeft,
  Download,
  Upload,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Loader2,
  FileJson,
  Diff,
  ShieldCheck,
  RefreshCw,
} from 'lucide-react';
import {
  usePreBundleStore,
  type BundleErrorEntry,
} from '../../stores/usePreBundleStore';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08 } },
};
const itemVariants = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.25 } },
};

export function PreBundleManager() {
  const {
    uploadedText,
    validateResult,
    diffResult,
    applyResult,
    isExporting,
    isValidating,
    isDiffing,
    isApplying,
    lastError,
    step,
    reset,
    setUploadedText,
    setStep,
    exportBundle,
    validate,
    diff,
    apply,
  } = usePreBundleStore();

  const [exportDescription, setExportDescription] = useState('');
  const [exportReleaseTag, setExportReleaseTag] = useState('');
  const [exportPretty, setExportPretty] = useState(true);
  const [confirmChecksum, setConfirmChecksum] = useState('');
  const [confirmSourceEnv, setConfirmSourceEnv] = useState('');
  const [approverNotes, setApproverNotes] = useState('');
  const [deactivateMissing, setDeactivateMissing] = useState(true);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleFile = async (file: File) => {
    const text = await file.text();
    setUploadedText(text);
  };

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) void handleFile(f);
    e.target.value = '';
  };

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) void handleFile(f);
  };

  const allowApply =
    validateResult &&
    diffResult &&
    confirmChecksum.trim().toLowerCase() ===
      (validateResult.checksum ?? '').toLowerCase() &&
    confirmSourceEnv.trim() === (validateResult.sourceEnv ?? '') &&
    approverNotes.trim().length > 0 &&
    !isApplying;

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="flex flex-col gap-6 p-6"
    >
      <motion.div variants={itemVariants} className="flex items-center gap-3">
        <Link
          to="/settings/policy"
          className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
        >
          <ArrowLeft className="h-4 w-4" /> Policy Engine
        </Link>
        <span className="text-slate-300 dark:text-slate-600">/</span>
        <h1 className="flex items-center gap-2 text-xl font-semibold text-slate-900 dark:text-slate-100">
          <Package className="h-5 w-5 text-blue-600 dark:text-blue-400" />
          PRE Bundle
        </h1>
      </motion.div>

      <motion.p
        variants={itemVariants}
        className="max-w-3xl text-sm text-slate-600 dark:text-slate-400"
      >
        A PRE bundle is a single canonical JSON file containing the entire
        executable rule corpus (jurisdictions, check definitions, rules,
        calling-hour windows, DNC registries, frequency caps, feature-flag
        defaults). Use export for backups and golden-master snapshots; use
        import to reconcile this environment to a reviewed bundle. Imports are
        atomic and never hard-delete: missing rules are soft-deactivated and
        all changes are versioned.
      </motion.p>

      {lastError && (
        <motion.div variants={itemVariants}>
          <ErrorBanner error={lastError} />
        </motion.div>
      )}

      {/* Export */}
      <motion.div variants={itemVariants}>
        <Card>
          <CardContent className="flex flex-col gap-4 p-6">
            <div className="flex items-center gap-2">
              <Download className="h-5 w-5 text-slate-700 dark:text-slate-300" />
              <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
                Export Bundle
              </h2>
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-400">
                  Description (optional)
                </label>
                <Input
                  value={exportDescription}
                  onChange={(e) => setExportDescription(e.target.value)}
                  placeholder="e.g. Pre-release snapshot for legal review"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-400">
                  Release tag (optional)
                </label>
                <Input
                  value={exportReleaseTag}
                  onChange={(e) => setExportReleaseTag(e.target.value)}
                  placeholder="e.g. v2.4.0-rc1"
                />
              </div>
            </div>
            <div>
              <Button
                onClick={() =>
                  void exportBundle({
                    description: exportDescription || undefined,
                    releaseTag: exportReleaseTag || undefined,
                    pretty: exportPretty,
                  })
                }
                disabled={isExporting}
              >
                {isExporting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Exporting…
                  </>
                ) : (
                  <>
                    <Download className="mr-2 h-4 w-4" /> Download Bundle
                  </>
                )}
              </Button>
              <label className="ml-3 inline-flex cursor-pointer items-center gap-2 text-xs text-slate-600 dark:text-slate-400">
                <input
                  type="checkbox"
                  checked={exportPretty}
                  onChange={(e) => setExportPretty(e.target.checked)}
                  className="h-3.5 w-3.5 rounded border-slate-300"
                />
                Pretty-print (human readable, round-trip safe)
              </label>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Import wizard */}
      <motion.div variants={itemVariants}>
        <Card>
          <CardContent className="flex flex-col gap-5 p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Upload className="h-5 w-5 text-slate-700 dark:text-slate-300" />
                <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
                  Import Bundle
                </h2>
              </div>
              <div className="flex items-center gap-1 text-xs">
                <StepBadge n={1} active={step === 1} done={step > 1} label="Upload" />
                <StepArrow />
                <StepBadge n={2} active={step === 2} done={step > 2} label="Validate" />
                <StepArrow />
                <StepBadge n={3} active={step === 3} done={step > 3} label="Diff" />
                <StepArrow />
                <StepBadge n={4} active={step === 4} done={false} label="Apply" />
              </div>
              {(uploadedText || validateResult) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    reset();
                    setConfirmChecksum('');
                    setConfirmSourceEnv('');
                    setApproverNotes('');
                  }}
                >
                  <RefreshCw className="mr-1 h-3 w-3" /> Start over
                </Button>
              )}
            </div>

            {/* Step 1: Upload */}
            {!uploadedText && (
              <div
                onDragOver={(e) => e.preventDefault()}
                onDrop={onDrop}
                onClick={() => fileInputRef.current?.click()}
                className={cn(
                  'flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 p-10 text-center transition-colors hover:border-blue-400 hover:bg-blue-50 dark:border-slate-700 dark:bg-slate-900/40 dark:hover:border-blue-500 dark:hover:bg-blue-900/10',
                )}
              >
                <FileJson className="h-8 w-8 text-slate-400" />
                <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  Drop a PRE bundle JSON here or click to browse
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  The file must be a canonical bundle exported from a Respectly
                  environment.
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="application/json,.json"
                  className="hidden"
                  onChange={onFileChange}
                />
              </div>
            )}

            {/* Step 2: Validate */}
            {uploadedText && !validateResult && (
              <div className="flex flex-col gap-3">
                <div className="rounded-md bg-slate-50 p-3 text-xs text-slate-600 dark:bg-slate-900/40 dark:text-slate-400">
                  Loaded {(uploadedText.length / 1024).toFixed(1)} KB. Click
                  Validate to verify checksum, schema, and referential
                  integrity. No changes are written.
                </div>
                <div>
                  <Button onClick={() => void validate()} disabled={isValidating}>
                    {isValidating ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Validating…
                      </>
                    ) : (
                      <>
                        <ShieldCheck className="mr-2 h-4 w-4" /> Validate
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}

            {/* Validate result */}
            {validateResult && (
              <ValidateSummary result={validateResult} />
            )}

            {/* Step 3: Diff */}
            {validateResult && !diffResult && (
              <div>
                <Button onClick={() => void diff()} disabled={isDiffing}>
                  {isDiffing ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Computing diff…
                    </>
                  ) : (
                    <>
                      <Diff className="mr-2 h-4 w-4" /> Compute Diff vs. Live DB
                    </>
                  )}
                </Button>
              </div>
            )}

            {diffResult && <DiffSummary result={diffResult} />}

            {/* Step 4: Apply */}
            {validateResult && diffResult && !applyResult && (
              <div className="flex flex-col gap-3 rounded-md border border-amber-200 bg-amber-50 p-4 dark:border-amber-900/40 dark:bg-amber-900/10">
                <div className="flex items-center gap-2 text-sm font-medium text-amber-900 dark:text-amber-200">
                  <AlertTriangle className="h-4 w-4" /> Confirm Apply
                </div>
                <p className="text-xs text-amber-800 dark:text-amber-300/80">
                  Apply runs in a single transaction. Rules absent from the
                  bundle will be soft-deactivated. Tenant overrides are not
                  touched. To proceed, type the bundle checksum and source
                  environment exactly.
                </p>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-xs font-medium text-slate-700 dark:text-slate-300">
                      Confirm checksum
                    </label>
                    <Input
                      value={confirmChecksum}
                      onChange={(e) => setConfirmChecksum(e.target.value)}
                      placeholder={validateResult.checksum}
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-slate-700 dark:text-slate-300">
                      Confirm source env
                    </label>
                    <Input
                      value={confirmSourceEnv}
                      onChange={(e) => setConfirmSourceEnv(e.target.value)}
                      placeholder={validateResult.sourceEnv}
                    />
                  </div>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-700 dark:text-slate-300">
                    Approver notes (required)
                  </label>
                  <Input
                    value={approverNotes}
                    onChange={(e) => setApproverNotes(e.target.value)}
                    placeholder="Ticket # or rationale recorded in audit log"
                  />
                </div>
                <label className="flex items-center gap-2 text-xs text-slate-700 dark:text-slate-300">
                  <input
                    type="checkbox"
                    checked={deactivateMissing}
                    onChange={(e) => setDeactivateMissing(e.target.checked)}
                  />
                  Deactivate rules missing from bundle (recommended)
                </label>
                <div>
                  <Button
                    onClick={() =>
                      void apply({
                        confirmChecksum,
                        confirmSourceEnv,
                        approverNotes,
                        deactivateMissing,
                      })
                    }
                    disabled={!allowApply}
                    className="bg-amber-600 hover:bg-amber-700"
                  >
                    {isApplying ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Applying…
                      </>
                    ) : (
                      <>Apply Bundle</>
                    )}
                  </Button>
                </div>
              </div>
            )}

            {applyResult && <ApplySummary result={applyResult} />}
          </CardContent>
        </Card>
      </motion.div>

      {/* Hidden helpers to keep eslint happy */}
      <button type="button" hidden onClick={() => setStep(1)} />
    </motion.div>
  );
}

// ============================================
// Subcomponents
// ============================================

function StepBadge({
  n,
  active,
  done,
  label,
}: {
  n: number;
  active: boolean;
  done: boolean;
  label: string;
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium',
        active && 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
        done && !active && 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
        !active && !done && 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400',
      )}
    >
      <span className="rounded-full bg-white/60 px-1 dark:bg-black/30">{n}</span>
      {label}
    </span>
  );
}
function StepArrow() {
  return <span className="text-slate-300 dark:text-slate-600">›</span>;
}

function ErrorBanner({
  error,
}: {
  error: { code: string; message: string; errors?: BundleErrorEntry[] };
}) {
  return (
    <div className="rounded-md border border-rose-200 bg-rose-50 p-4 dark:border-rose-900/40 dark:bg-rose-900/10">
      <div className="flex items-center gap-2 text-sm font-semibold text-rose-800 dark:text-rose-300">
        <XCircle className="h-4 w-4" /> {error.code}
      </div>
      <p className="mt-1 text-xs text-rose-700 dark:text-rose-300/80">
        {error.message}
      </p>
      {error.errors && error.errors.length > 0 && (
        <ul className="mt-2 max-h-48 overflow-auto text-[11px] text-rose-700 dark:text-rose-300/80">
          {error.errors.slice(0, 50).map((e, i) => (
            <li key={i} className="border-t border-rose-200/60 py-1 first:border-0 dark:border-rose-900/30">
              <span className="font-mono text-rose-900 dark:text-rose-200">
                {e.code}
              </span>
              {e.path && (
                <span className="ml-1 font-mono text-rose-600 dark:text-rose-400">
                  {e.path}
                </span>
              )}
              <span className="ml-1">{e.message}</span>
            </li>
          ))}
          {error.errors.length > 50 && (
            <li className="pt-1 italic">
              +{error.errors.length - 50} more error(s)…
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

function ValidateSummary({
  result,
}: {
  result: NonNullable<ReturnType<typeof usePreBundleStore.getState>['validateResult']>;
}) {
  return (
    <div className="rounded-md border border-emerald-200 bg-emerald-50 p-4 dark:border-emerald-900/40 dark:bg-emerald-900/10">
      <div className="flex items-center gap-2 text-sm font-semibold text-emerald-800 dark:text-emerald-300">
        <CheckCircle2 className="h-4 w-4" /> Bundle is valid
      </div>
      <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-emerald-900 dark:text-emerald-200 md:grid-cols-4">
        <Field label="Source env" value={result.sourceEnv} />
        <Field label="Bundle id" value={result.bundleId.slice(0, 8) + '…'} />
        <Field label="Checksum" value={result.checksum.slice(0, 12) + '…'} mono />
        <Field label="Generated at" value={result.generatedAt.slice(0, 19) + 'Z'} />
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2 text-[11px] md:grid-cols-4">
        <Count label="Rules" n={result.counts.rules} />
        <Count label="Jurisdictions" n={result.counts.jurisdictions} />
        <Count label="Checks" n={result.counts.checkDefinitions} />
        <Count label="Calling hours" n={result.counts.callingHourWindows} />
        <Count label="DNC registries" n={result.counts.dncRegistries} />
        <Count label="Freq caps" n={result.counts.frequencyCaps} />
        <Count label="Flag defaults" n={result.counts.featureFlagDefaults} />
      </div>
      {result.warnings.length > 0 && (
        <div className="mt-3 rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800 dark:border-amber-900/40 dark:bg-amber-900/10 dark:text-amber-300">
          <div className="flex items-center gap-1 font-medium">
            <AlertTriangle className="h-3 w-3" /> {result.warnings.length} warning(s)
          </div>
          <ul className="mt-1 list-disc pl-4">
            {result.warnings.slice(0, 5).map((w, i) => (
              <li key={i}>
                <span className="font-mono">{w.code}</span> — {w.message}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function DiffSummary({
  result,
}: {
  result: NonNullable<ReturnType<typeof usePreBundleStore.getState>['diffResult']>;
}) {
  const sections: Array<keyof typeof result.diff> = [
    'jurisdictions',
    'checkDefinitions',
    'rules',
    'callingHourWindows',
    'dncRegistries',
    'frequencyCaps',
    'featureFlagDefaults',
  ];
  return (
    <div className="flex flex-col gap-3">
      <div className="text-sm font-semibold text-slate-800 dark:text-slate-200">
        Diff vs. live DB {result.diff.isEmpty && (
          <Badge className="ml-2 bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
            no changes
          </Badge>
        )}
      </div>
      <div className="overflow-hidden rounded-md border border-slate-200 dark:border-slate-700">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-left text-[11px] uppercase text-slate-500 dark:bg-slate-900/40 dark:text-slate-400">
            <tr>
              <th className="px-3 py-2">Section</th>
              <th className="px-3 py-2 text-right">Added</th>
              <th className="px-3 py-2 text-right">Modified</th>
              <th className="px-3 py-2 text-right">Soft-Deactivate</th>
              <th className="px-3 py-2 text-right">Unchanged</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
            {sections.map((s) => {
              const sec = result.diff[s] as {
                added: unknown[];
                removed: unknown[];
                modified: unknown[];
                unchangedCount: number;
              };
              return (
                <tr key={s}>
                  <td className="px-3 py-2 font-medium text-slate-700 dark:text-slate-300">
                    {s}
                  </td>
                  <td className="px-3 py-2 text-right text-emerald-700 dark:text-emerald-400">
                    {sec.added.length}
                  </td>
                  <td className="px-3 py-2 text-right text-amber-700 dark:text-amber-400">
                    {sec.modified.length}
                  </td>
                  <td className="px-3 py-2 text-right text-rose-700 dark:text-rose-400">
                    {sec.removed.length}
                  </td>
                  <td className="px-3 py-2 text-right text-slate-500 dark:text-slate-400">
                    {sec.unchangedCount}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ApplySummary({
  result,
}: {
  result: NonNullable<ReturnType<typeof usePreBundleStore.getState>['applyResult']>;
}) {
  return (
    <div className="rounded-md border border-emerald-200 bg-emerald-50 p-4 dark:border-emerald-900/40 dark:bg-emerald-900/10">
      <div className="flex items-center gap-2 text-sm font-semibold text-emerald-800 dark:text-emerald-300">
        <CheckCircle2 className="h-4 w-4" /> Apply succeeded
      </div>
      <div className="mt-1 text-[11px] text-emerald-900 dark:text-emerald-200">
        Bundle <span className="font-mono">{result.bundleId.slice(0, 8)}…</span>{' '}
        applied in {result.durationMs} ms.
      </div>
      <pre className="mt-2 max-h-64 overflow-auto rounded bg-white/60 p-2 text-[11px] text-slate-800 dark:bg-black/30 dark:text-slate-200">
        {JSON.stringify(result.rowsAffected, null, 2)}
      </pre>
    </div>
  );
}

function Field({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-emerald-700/70 dark:text-emerald-400/70">
        {label}
      </div>
      <div className={cn('truncate', mono && 'font-mono')}>{value}</div>
    </div>
  );
}
function Count({ label, n }: { label: string; n: number }) {
  return (
    <div className="rounded bg-white/60 px-2 py-1 dark:bg-black/30">
      <div className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {label}
      </div>
      <div className="text-sm font-semibold text-slate-800 dark:text-slate-200">{n}</div>
    </div>
  );
}
